import { ApiError } from "../api/http_client.js";
import { getConfigHashes } from "../api/requests/player.requests.js";
import { NetworkManager } from "../network_manager.js";
import { ConfigHashStore } from "./config_hash_store.js";
import { computeStaleConfigTypes, hashesWithoutCache, type CachedConfigCheck, type ConfigHashMap } from "./config_staleness.js";

export type ConfigRefresher = ( stale_types: string[] ) => Promise<unknown>;

export interface ConfigSyncResult
{
  reason: string;
  checked: boolean;
  hashes_available: boolean;
  stale: string[];
  failed: string[];
  incomplete: string[];
}

const FOREGROUND_MIN_INTERVAL_MS = 60_000;

export class ConfigSyncService
{
  private static refreshers = new Map<string, ConfigRefresher>();
  private static cache_checks = new Map<string, CachedConfigCheck>();
  private static running: Promise<ConfigSyncResult> | null = null;
  private static last_checked_at = 0;

  public static register( types: string[], refresh: ConfigRefresher, is_cached?: CachedConfigCheck ): void
  {
    for ( const type of types )
    {
      this.refreshers.set( type, refresh );
      if ( is_cached ) this.cache_checks.set( type, is_cached );
    }
  }

  public static reconcileHashes(): string[]
  {
    const orphaned = hashesWithoutCache( ConfigHashStore.getAll(), this.cache_checks );
    for ( const type of orphaned ) ConfigHashStore.remove( type );
    if ( orphaned.length ) console.warn( `[ConfigSync] dropped hash for ${orphaned.join( ", " )}: cached payload missing, will refetch` );
    return orphaned;
  }

  public static knownHashes(): ConfigHashMap
  {
    this.reconcileHashes();
    return { ...ConfigHashStore.getAll() };
  }

  public static registeredTypes(): string[]
  {
    return [ ...this.refreshers.keys() ];
  }

  public static computeStale( server: ConfigHashMap | null, local: ConfigHashMap, types: readonly string[] ): string[]
  {
    return computeStaleConfigTypes( server, local, types );
  }

  public static refreshAll( reason: string ): Promise<ConfigSyncResult>
  {
    return this.run( reason, { force_all: true } );
  }

  public static refreshStale( reason: string, options: { throttle?: boolean } = {} ): Promise<ConfigSyncResult>
  {
    if ( options.throttle && Date.now() - this.last_checked_at < FOREGROUND_MIN_INTERVAL_MS )
      return Promise.resolve( this.skipped( reason ) );
    return this.run( reason, { force_all: false } );
  }

  private static skipped( reason: string ): ConfigSyncResult
  {
    return { reason, checked: false, hashes_available: false, stale: [], failed: [], incomplete: [] };
  }

  private static run( reason: string, options: { force_all: boolean } ): Promise<ConfigSyncResult>
  {
    if ( this.running ) return this.running;
    this.running = this.runOnce( reason, options ).finally( () => { this.running = null; } );
    return this.running;
  }

  private static async runOnce( reason: string, options: { force_all: boolean } ): Promise<ConfigSyncResult>
  {
    if ( !NetworkManager.isOnline() ) return this.skipped( reason );

    this.reconcileHashes();
    const types = this.registeredTypes();
    let server: ConfigHashMap | null = null;
    if ( !options.force_all )
    {
      try
      {
        server = ( await getConfigHashes() ).hashes;
        this.last_checked_at = Date.now();
      }
      catch ( e )
      {
        if ( e instanceof ApiError && e.status === 404 )
          console.info( "[ConfigSync] server has no config-hashes route, refreshing every config" );
        else
          console.warn( "[ConfigSync] hash check failed, refreshing every config", e );
      }
    }

    const stale = this.computeStale( server, ConfigHashStore.getAll(), types );
    console.info( `[ConfigSync] ${reason}: ${stale.length}/${types.length} config(s) stale${stale.length ? ` — ${stale.join( ", " )}` : ""}` );

    const failed: string[] = [];
    const jobs = new Map<ConfigRefresher, string[]>();
    for ( const type of stale )
    {
      const refresher = this.refreshers.get( type );
      if ( !refresher ) continue;
      const group = jobs.get( refresher ) ?? [];
      group.push( type );
      jobs.set( refresher, group );
    }

    await Promise.all( [ ...jobs ].map( async ( [ refresher, group ] ) =>
    {
      try
      {
        await refresher( group );
      }
      catch ( e )
      {
        failed.push( ...group );
        console.warn( `[ConfigSync] refresh failed for ${group.join( ", " )}`, e );
      }
    } ) );

    if ( server )
    {
      for ( const type of stale )
      {
        if ( server[type] === undefined && !failed.includes( type ) )
          ConfigHashStore.remove( type );
      }
    }

    const incomplete = server
      ? types.filter( type => server[type] !== undefined && ConfigHashStore.get( type ) !== server[type] )
      : [ ...failed ];
    if ( incomplete.length ) console.warn( `[ConfigSync] still missing after refresh: ${incomplete.join( ", " )}` );

    return { reason, checked: true, hashes_available: server !== null, stale, failed, incomplete };
  }
}
